import React, { useState, useEffect } from 'react';
import { API } from '../App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { FileDown, Search, Filter, FileSpreadsheet, Eye } from 'lucide-react';

function Reports() {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    plate_number: '',
    status: '',
    start_date: '',
    end_date: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.plate_number) params.append('plate_number', filters.plate_number);
      if (filters.status) params.append('status', filters.status);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axios.get(`${API}/inspections?${params.toString()}`);
      setInspections(response.data);
    } catch (error) {
      toast.error('Failed to load inspections');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleSearch = () => {
    setLoading(true);
    fetchInspections();
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.plate_number) params.append('plate_number', filters.plate_number);
      if (filters.status) params.append('status', filters.status);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axios.get(`${API}/export/excel?${params.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inspections_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Excel file downloaded successfully');
    } catch (error) {
      toast.error('Failed to export to Excel');
    }
  };

  const handleExportPDF = async (inspectionId, plateNumber, date) => {
    try {
      const response = await axios.get(`${API}/export/pdf/${inspectionId}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inspection_${plateNumber}_${date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to export to PDF');
    }
  };

  const handleReset = () => {
    setFilters({
      plate_number: '',
      status: '',
      start_date: '',
      end_date: '',
    });
    setTimeout(() => {
      setLoading(true);
      fetchInspections();
    }, 100);
  };

  return (
    <div className="space-y-6 fade-in" data-testid="reports-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Inspection Reports</h1>
          <p className="text-gray-600 mt-1">View, filter, and export inspection records</p>
        </div>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-orange-50">
          <CardTitle className="flex items-center">
            <Filter className="w-5 h-5 mr-2 text-blue-600" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Plate Number</label>
              <Input
                placeholder="Search plate number"
                data-testid="filter-plate-number-input"
                value={filters.plate_number}
                onChange={(e) => handleFilterChange('plate_number', e.target.value)}
                className="h-10"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Status</label>
              <Select value={filters.status} onValueChange={(value) => handleFilterChange('status', value)}>
                <SelectTrigger data-testid="filter-status-select" className="h-10">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Pass">Pass</SelectItem>
                  <SelectItem value="Service Required">Service Required</SelectItem>
                  <SelectItem value="Failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Start Date</label>
              <Input
                type="date"
                data-testid="filter-start-date-input"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="h-10"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">End Date</label>
              <Input
                type="date"
                data-testid="filter-end-date-input"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="h-10"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button
              onClick={handleSearch}
              data-testid="search-button"
              className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-6"
            >
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button
              onClick={handleReset}
              variant="outline"
              data-testid="reset-filters-button"
              className="h-10 px-6"
            >
              Reset
            </Button>
            <Button
              onClick={handleExportExcel}
              variant="outline"
              data-testid="export-excel-button"
              className="h-10 px-6 ml-auto"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export to Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-lg">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading inspections...</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="inspections-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Plate Number</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Driver</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Inspector</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {inspections.length > 0 ? (
                    inspections.map((inspection, index) => (
                      <tr key={index} data-testid={`inspection-row-${index}`} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{inspection.plate_number}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{inspection.driver_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{inspection.inspector_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{inspection.inspection_date}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`status-badge ${
                            inspection.overall_status === 'Pass' ? 'status-pass' :
                            inspection.overall_status === 'Service Required' ? 'status-service' :
                            'status-failed'
                          }`}>
                            {inspection.overall_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            <Button
                              onClick={() => navigate(`/inspection/${inspection.id}`)}
                              size="sm"
                              variant="outline"
                              data-testid={`view-button-${index}`}
                              className="h-8 text-xs"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View
                            </Button>
                            <Button
                              onClick={() => handleExportPDF(inspection.id, inspection.plate_number, inspection.inspection_date)}
                              size="sm"
                              variant="outline"
                              data-testid={`pdf-button-${index}`}
                              className="h-8 text-xs"
                            >
                              <FileDown className="w-3 h-3 mr-1" />
                              PDF
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        No inspections found. Try adjusting your filters or create a new inspection.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default Reports;